import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import type { EntityCrossFilter } from '@/utils/entityCrossFilter';
import type { AdProductRow, AdvertisedProductRow, ProductAdCatalogRow } from '@/utils/workspaceProductMetrics';
import { emptyProductMetrics } from '@/utils/workspaceProductMetrics';
import type { WorkspacePageSize } from '@/hooks/useServerPagination';
import type { MetricFilterPayload } from '@/utils/workspaceMetricFilters';

function parseImageUrl(row: Record<string, unknown>): string | null {
  const value = row.imageUrl ?? row.image_url;
  return typeof value === 'string' && value.trim() ? value : null;
}

function parseAdProductsPage(data: unknown): {
  rows: AdProductRow[];
  totalCount: number;
  catalog: ProductAdCatalogRow[];
} {
  if (!data || typeof data !== 'object') {
    return { rows: [], totalCount: 0, catalog: [] };
  }
  const raw = data as Record<string, unknown>;
  const rows = Array.isArray(raw.rows)
    ? (raw.rows as Record<string, unknown>[]).map((row) => ({
        ...(row as unknown as AdProductRow),
        imageUrl: parseImageUrl(row),
        metrics: (row.metrics as AdProductRow['metrics']) ?? emptyProductMetrics(),
      }))
    : [];
  const catalog = Array.isArray(raw.catalog) ? (raw.catalog as ProductAdCatalogRow[]) : [];
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount, catalog };
}

function parseAdvertisedProductsPage(data: unknown): {
  rows: AdvertisedProductRow[];
  totalCount: number;
  catalog: ProductAdCatalogRow[];
} {
  if (!data || typeof data !== 'object') {
    return { rows: [], totalCount: 0, catalog: [] };
  }
  const raw = data as Record<string, unknown>;
  const rows = Array.isArray(raw.rows)
    ? (raw.rows as Record<string, unknown>[]).map((row) => ({
        ...(row as unknown as AdvertisedProductRow),
        imageUrl: parseImageUrl(row),
        adMetrics: (row.adMetrics as AdvertisedProductRow['adMetrics']) ?? emptyProductMetrics(),
      }))
    : [];
  const catalog = Array.isArray(raw.catalog) ? (raw.catalog as ProductAdCatalogRow[]) : [];
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount, catalog };
}

export function useWorkspaceAdProductsPage({
  profileId,
  appliedFilter,
  page,
  pageSize,
  search,
  status,
  sortBy,
  sortDir,
  startDate,
  endDate,
  metricFilters,
  enabled,
}: {
  profileId: string;
  appliedFilter: EntityCrossFilter;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  metricFilters?: MetricFilterPayload[];
  enabled: boolean;
}) {
  const offset = (page - 1) * pageSize;
  const queryEnabled = Boolean(profileId) && enabled;

  const query = useQuery({
    queryKey: [
      'workspace-ad-products-page',
      profileId,
      pageSize,
      offset,
      appliedFilter,
      search,
      status,
      sortBy ?? '',
      sortDir ?? 'asc',
      startDate ?? '',
      endDate ?? '',
      metricFilters ?? [],
    ],
    queryFn: async () => {
      const res = await adsApi.getWorkspaceAdProductsPage(profileId, {
        limit: pageSize,
        offset,
        appliedFilter: appliedFilter as unknown as Record<string, unknown>,
        state: status !== 'ALL' ? status : undefined,
        search: search.trim() || undefined,
        sortBy,
        sortDir,
        startDate,
        endDate,
        metricFilters: metricFilters && metricFilters.length > 0 ? metricFilters : undefined,
      });
      if (!res.success) throw new Error('Failed to load ad products');
      return parseAdProductsPage(res.data);
    },
    enabled: queryEnabled,
  });

  return {
    rows: query.data?.rows ?? [],
    totalCount: query.data?.totalCount ?? 0,
    catalog: query.data?.catalog ?? [],
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

export function useWorkspaceAdvertisedProductsPage({
  profileId,
  appliedFilter,
  page,
  pageSize,
  search,
  status,
  sortBy,
  sortDir,
  startDate,
  endDate,
  metricFilters,
  enabled,
}: {
  profileId: string;
  appliedFilter: EntityCrossFilter;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  metricFilters?: MetricFilterPayload[];
  enabled: boolean;
}) {
  const offset = (page - 1) * pageSize;
  const queryEnabled = Boolean(profileId) && enabled;

  const query = useQuery({
    queryKey: [
      'workspace-advertised-products-page',
      profileId,
      pageSize,
      offset,
      appliedFilter,
      search,
      status,
      sortBy ?? '',
      sortDir ?? 'asc',
      startDate ?? '',
      endDate ?? '',
      metricFilters ?? [],
    ],
    queryFn: async () => {
      const res = await adsApi.getWorkspaceAdvertisedProductsPage(profileId, {
        limit: pageSize,
        offset,
        appliedFilter: appliedFilter as unknown as Record<string, unknown>,
        state: status !== 'ALL' ? status : undefined,
        search: search.trim() || undefined,
        sortBy,
        sortDir,
        startDate,
        endDate,
        metricFilters: metricFilters && metricFilters.length > 0 ? metricFilters : undefined,
      });
      if (!res.success) throw new Error('Failed to load advertised products');
      return parseAdvertisedProductsPage(res.data);
    },
    enabled: queryEnabled,
  });

  return {
    rows: query.data?.rows ?? [],
    totalCount: query.data?.totalCount ?? 0,
    catalog: query.data?.catalog ?? [],
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
